import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { useServiceCategories } from '../../services/serviceCategoryService';
import { CategoryGlyph, resolveCategoryIcon, usableImageUrl } from '../shared/categoryIcons';
import MarketingReveal from './MarketingReveal';

/**
 * Day-polarity category strip — cream canvas, thin title, quiet chips / icon tiles.
 * Keeps existing category browse; adds icon tiles + rail fades when available.
 */
const ServiceCategories = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const { data: categoriesData, isLoading } = useServiceCategories({
    active: true,
    sort_by: 'sort_order',
    sort_order: 'asc',
    per_page: 20,
  });

  const categories = categoriesData?.data?.data || [];

  const updateArrows = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 8);
  };

  useEffect(() => {
    updateArrows();
    const el = scrollRef.current;
    if (!el) return undefined;
    el.addEventListener('scroll', updateArrows, { passive: true });
    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [categories.length]);

  const scrollBy = (dir) => {
    scrollRef.current?.scrollBy({ left: dir * 280, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <section className="bg-m-canvas-light py-24 sm:py-28">
        <div className="page-container">
          <h2 className="marketing-section-title mb-4 text-m-ink">
            Everything you need for your event
          </h2>
          <p className="marketing-body text-m-ink-muted">Loading categories…</p>
        </div>
      </section>
    );
  }

  if (!categories.length) return null;

  return (
    <section className="border-b border-m-border-light bg-m-canvas-light py-24 sm:py-28">
      <MarketingReveal className="page-container">
        <div className="mb-14 flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between">
          <div className="max-w-2xl">
            <h2 className="marketing-section-title mb-4 text-m-ink">
              Everything you need for your event
            </h2>
            <p className="marketing-body-lg text-m-ink-muted">
              Pick a category, or open the full photo-led vendor list.
            </p>
          </div>
          <Link
            to="/browse-vendors"
            className="group inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-m-ink transition-opacity hover:opacity-70"
          >
            Browse all vendors
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </Link>
        </div>

        <div className="marketing-rail marketing-rail-cream relative">
          {canScrollLeft ? (
            <button
              type="button"
              onClick={() => scrollBy(-1)}
              className="absolute -left-1 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-m-border-light bg-m-surface text-m-ink shadow-sm transition-colors hover:border-m-ink/30 sm:flex"
              aria-label="Scroll categories left"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
          ) : null}
          {canScrollRight ? (
            <button
              type="button"
              onClick={() => scrollBy(1)}
              className="absolute -right-1 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-m-border-light bg-m-surface text-m-ink shadow-sm transition-colors hover:border-m-ink/30 sm:flex"
              aria-label="Scroll categories right"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          ) : null}

          <div
            ref={scrollRef}
            className="flex gap-4 overflow-x-auto pb-1 scrollbar-hide"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            {categories.map((category) => {
              const href = `/category/${category.id}?title=${encodeURIComponent(
                category.category_name || 'Services'
              )}`;

              return (
                <CategoryTile key={category.id} category={category} href={href} />
              );
            })}
          </div>
        </div>
      </MarketingReveal>
    </section>
  );
};

function CategoryTile({ category, href }) {
  const imageUrl = usableImageUrl(category.icon_url);
  const [imageFailed, setImageFailed] = useState(false);
  const showImage = Boolean(imageUrl) && !imageFailed;

  return (
    <Link to={href} className="group flex w-[7.5rem] shrink-0 flex-col sm:w-32">
      <div className={`mb-3 flex aspect-square items-center justify-center overflow-hidden rounded-2xl border border-m-border-light bg-m-surface transition-all duration-300 group-hover:border-m-ink/40 group-hover:shadow-[0_12px_28px_rgba(0,0,0,0.08)] ${showImage ? '' : 'p-3'}`}>
        {showImage ? (
          <img
            src={imageUrl}
            alt=""
            className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <CategoryGlyph iconId={resolveCategoryIcon(category)} className="h-16 w-16 sm:h-[4.5rem] sm:w-[4.5rem]" />
        )}
      </div>
      <span className="line-clamp-2 text-center text-sm font-medium tracking-tight text-m-ink">
        {category.category_name}
      </span>
    </Link>
  );
}

export default ServiceCategories;
